import { Links } from "@/redux/types";

/**
 * POI tag values, mirroring the mobile `RMTags` enum (src/enums/FavouriteType.ts).
 * These are the exact backend strings — note they are prefixed/lowercased
 * (e.g. "location_other", not "Other"), and must match so favourites are recognised
 * across web and mobile.
 */
export const RM_TAGS = {
  Home: "location_home",
  Work: "location_work",
  Other: "location_other",
  Recent: "location_recent",
  Airport: "airport",
  Hotel: "hotel",
  InternationalTerminal: "international_terminal",
  FixedPin: "fixed_pin",
  ShipTerminal: "ship_terminal",
  MajorPoi: "major_poi",
} as const;

/** The Major POI tag value used by the backend (matches mobile RMTags.Major_POI). */
export const MAJOR_POI_TAG = RM_TAGS.MajorPoi;

/**
 * A point of interest as stored/returned by /v3/client/{accountId}/location/poi.
 * Mirrors the mobile `PointsOfInterest` shape (snake_case — sent/received as-is).
 */
export type PointsOfInterest = {
  uuid: string;
  vendor: string;
  vendor_uuid: string;
  vendor_address: string;
  address: string;
  lat: number;
  lng: number;
  city: string;
  postcode: string;
  state: string;
  state_code: string;
  country: string;
  country_code: string;
  rm_label: string;
  rm_type: string;
  rm_tags: string[];
  rm_exclusions: string[];
};

/** HAL collection envelope returned by the POI list endpoints. */
export type ListPoisRawResponse = {
  _embedded?: { items?: PointsOfInterest[] };
  _links?: Links;
  page_count?: number;
  page_size?: number;
  total_items?: number;
  page?: number;
};

export type ClientScopeArg = { accountId?: string };

export type GetClientPoisArgs = ClientScopeArg & {
  page: number;
  pageSize: number;
  sort?: string;
  order?: "asc" | "desc";
};

export type GetClientPoisResult = {
  items: PointsOfInterest[];
  totalItems: number;
  pageCount: number;
  page: number;
};

/** Body for create/update. `uuid` only present on update. */
export type SaveClientPoiArgs = Partial<PointsOfInterest> &
  ClientScopeArg & {
    rm_label: string;
    address: string;
    lat: number;
    lng: number;
  };

export type RemoveClientPoiArgs = ClientScopeArg & { poiId: string };
